import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { useTranslation } from '../../contexts/TranslationContext';
import useApi from '../../api';
import { FileMetadata, FileRemovalProbe } from '../../api/types/cms';
import { useResponseHandler } from '../../utils/response';
import { useEffect, useState } from 'react';
import {
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import FileInfo from '../cms/FileInfo';
import { useSnackbar } from 'notistack';

type FileDeleteDialogProps = {
    file?: FileMetadata | null;
    onClose?: (deleted: boolean) => void;
};

export default function FileDeleteDialog({
    file,
    onClose = () => {},
}: FileDeleteDialogProps) {
    const { cms } = useApi();
    const { translate } = useTranslation();
    const { handleResponse } = useResponseHandler();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<FileRemovalProbe>({
        removable: true,
        usages: [],
    });

    useEffect(() => {
        if (file) {
            setLoading(true);
            cms.removalProbe(file.file_id).then(response => {
                setLoading(false);
                handleResponse(response, () => {
                    if (response) {
                        setData(response);
                    }
                });
            });
        }
    }, [file, cms]);

    const deleteAnyWay = () => {
        if (file) {
            cms.deleteFileByFileId(file.file_id).then(response => {
                handleResponse(response, () => {
                    enqueueSnackbar(translate('messages.deleted'), {
                        variant: 'success',
                    });
                    onClose(response?.success ?? false);
                });
            });
        }
    };

    const openUsage = (usage: { class: string; id: string }) => {
        switch (usage.class) {
            case 'Partner':
                window.open(`/partner/${usage.id}/cms`, '_blank');
                break;
            case 'Page':
                window.open(`/cms/pages/${usage.id}/edit`, '_blank');
                break;
            case 'User':
                window.open(`/admin/user/${usage.id}`, '_blank');
                break;
        }
    };

    const renderUsage = (usage: any, index: number) => (
        <ListItemButton key={`usage-${index}`} onClick={() => openUsage(usage)}>
            <ListItemIcon sx={{ mr: 2 }}>
                <WarningOutlined style={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
                primaryTypographyProps={{ color: 'warning.main' }}
                primary={usage.meta.name}
                secondary={`${translate(`models.entity.${usage.class.toLowerCase()}`)} • ${usage.id}`}
            />
        </ListItemButton>
    );

    return (
        <Dialog open={Boolean(file)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle variant="h5">
                {translate('pages.cms.files.actions.delete')}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={1} sx={{ flex: 1 }}>
                    {file && <FileInfo file={file} global />}
                    {loading ? (
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            minHeight={100}
                            spacing={1}>
                            <LoadingOutlined style={{ fontSize: 24 }} />
                            <Stack alignItems="center">
                                <Typography
                                    variant="caption"
                                    color="text.secondary">
                                    {translate(
                                        'pages.cms.files.delete.loading_connections',
                                    )}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.primary">
                                    {translate('messages.please_wait')}
                                </Typography>
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack spacing={0}>
                            {!data.removable ? (
                                <>
                                    <div>
                                        <Typography
                                            variant="overline"
                                            fontWeight="bold"
                                            fontSize={14}
                                            color="error.main">
                                            {translate('messages.alert')}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary">
                                            {translate(
                                                'pages.cms.files.delete.existing_connections',
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary">
                                            {translate(
                                                'pages.cms.files.delete.disconnect_connections',
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary">
                                            {translate(
                                                'pages.cms.files.delete.delete_not_recommended',
                                            )}
                                        </Typography>
                                    </div>
                                    <List>{data.usages.map(renderUsage)}</List>
                                </>
                            ) : (
                                <>
                                    <Typography
                                        variant="overline"
                                        fontWeight="bold"
                                        fontSize={14}
                                        color="success.main">
                                        {translate(
                                            'pages.cms.files.delete.no_connections',
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary">
                                        {translate(
                                            'pages.cms.files.delete.delete_is_ok',
                                        )}
                                    </Typography>
                                </>
                            )}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={() => onClose(false)}>
                    {translate('form.general.cancel')}
                </Button>
                <Button
                    variant="contained"
                    disabled={loading}
                    onClick={deleteAnyWay}>
                    {data.removable
                        ? translate('form.general.delete')
                        : translate('form.general.delete_anyway')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
